import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createSearchParams } from "../navigation";

import { useMessages, ErrorMessage } from "../Messages"
import { AgorumObject } from "./DocumentsProvider"
import { commodity, competitor_family, competitor_member, design_family, design_registration, family_member, invention, patent_family, trade_mark, trademark_family } from "../data";
import { emptyStringAsUndefined } from "../utils/strings";
import { useAuth } from "../user/Auth";
import _ from "lodash";
import { familyUrl, memberUrl } from "../patents/utils";
import { trademarkFamilyUrl, trademarkUrl } from "../trademarks/utils";
import { inventionUrl } from "../inventions/utils";
import { commodityReferenceUrl } from "../products/products";
import { htmlDecode, htmlEncode } from "./encoding";
import { designFamilyUrl, designRegistrationUrl } from "src/designs/utils";
import { competitorFamilyUrl, competitorMemberUrl } from "src/external/utils";

const identifier = 'client-areas'
const folderLookup = {
    [patent_family]: 'Patents',
    [family_member]: 'Patents',
    [trademark_family]: 'Trademarks',
    [trade_mark]: 'Trademarks',
    [invention]: 'Inventions',
    [commodity]: 'Products',
    [design_family]: 'Designs',
    [design_registration]: 'Designs',
    [competitor_member]: 'Patent Monitoring',
    [competitor_family]: 'Patent Monitoring',
}

const reverseLookup = {
    'Patents': { type: patent_family, baseurl: '/patents/portfolio', url: (internalReference: string) => familyUrl({internalReference}), memberurl: (internalReference: string) => memberUrl({internalReference}) },
    'Trademarks': { type: trademark_family, baseurl: '/trademarks', url: (reference: string) => trademarkFamilyUrl({reference}), memberurl: (reference: string) => trademarkUrl({reference}) },
    'Inventions': { type: invention, baseurl: '/inventions', url: (reference: string) => inventionUrl({reference}) },
    'Products': { type: commodity, baseurl: '/products', url: () => '/products/portfolio', memberurl: (reference: string) => commodityReferenceUrl({reference}) },
    'Designs': { type: design_family, baseurl: '/designs', url: (reference: string) => designFamilyUrl({reference}), memberurl: (reference: string) => designRegistrationUrl({reference}) },
    'Patent Monitoring': { type: competitor_family, baseurl: '', url: (internalReference: string) => competitorFamilyUrl({internalReference}), memberurl: (internalReference: string) => competitorMemberUrl({internalReference})}
}

export function analysePath(path: string) {
    const parts = (path.split(identifier)?.[1]?.split('/')?.slice(5) ?? []).map(htmlDecode)
    if (parts.length === 0) return undefined

    const logic = reverseLookup[parts[0]]
    if (!logic) return undefined

    // IP Type
    if (parts.length === 1) return {url: logic.baseurl, parts}

    //console.log({path, parts})

    const url = parts.length >= 3 && logic.memberurl !== undefined 
        ? logic.memberurl(parts[2]) // Member
        : logic.url(parts[1]) // Family
    return {url, parts}
}
// 1: see if you find family 
// if not create family folder
// return family folder
// 
// see if you find member
// if not, go to 1
// create member folder

async function findIpRootFolder({number, entity}: {number: string, entity: string}) {
    // Objects in trash are not in path 9999
    // ci: case insensitive; "" because of spaces
    const query = `infolder:\${ID:${getClientRoot(number)}} name_ci:"${folderLookup[entity]}"`
    const searchResult = await folderSearch(encodeURIComponent(query))
    const uuid = searchResult?.[0]?.uuid
    if (!uuid) {
        const error = `Could not find folder for ${entity}`
        console.warn(error)
        //throw new Error(error)
    }
    return uuid
}

export async function createFamilyFolder({number, entity, internalReference, description}: {number: string, entity: string, internalReference: string, description?: string}) {
    const uuid = await findIpRootFolder({number, entity})
    const result = await addFolder({uuid, foldername: internalReference, description})
    // TODO: This is currently only the id. Can we change that?
    //console.log({result})
    return result
}

function createSearchKey(entity: string, internalReference: string) {
    return ['search-uuid', entity, internalReference]
}

export function useInvalidateFindUuid(entity: string, internalReference: string) {
    const client = useQueryClient()
    return () => client.invalidateQueries({queryKey: createSearchKey(entity, internalReference)})
}

export async function findUuid(number: string, entity: string, internalReference: string, parent?: string) {
    const inpath = parent 
        ? `${folderLookup[entity]}/${parent}`
        : folderLookup[entity]
    const query = `infolder:\${ID:${getClientRoot(number)}/${inpath}} name_ci:"${internalReference}"`
    //console.log({query})
    const searchResult = await folderSearch(encodeURIComponent(query))
    return searchResult?.[0]?.uuid
}

// https://agorumdocproxy.agorum.com/roiwebui/acds_module/overview2/index.html#/7a345050-e913-11e9-a3bc-005056aa0ecc/ecba9c30-e7f1-11ec-bc0b-005056aa0ecc
// infolder:${ID:/agorum/roi/files/client-areas/10/00/35/client_100035/Patents/P-003}
// classname:(noteobject OR folderobject)
export function useFindUuid(number: string, entity: string, internalReference: string, parent?: string) {
        // const enabled = !!internalReference && !!number && !!entity
        // console.log({enabled, number, entity, internalReference, parent})
    const {data, isLoading} = useQuery({
        queryKey: createSearchKey(entity, internalReference),
        //() => search(encodeURIComponent(query), ['inTrash']),
        queryFn: () => findUuid(number, entity, htmlEncode(internalReference), htmlEncode(parent)).then(r => r ? r : ''),
        enabled: !!internalReference && !!number && !!entity
    })
    //console.log(data)
    return {uuid: emptyStringAsUndefined(data), isLoading}
}

type DynamicProperties<T extends string> = {
    [P in T]: any
}
async function folderSearch<K extends string>(query: string, properties: K[] = []): Promise<(AgorumObject & DynamicProperties<K>)[]> {
    const params = createSearchParams({query, provider: 'search'})
    //console .log(params)
    return parseResponse(fetch(`/api/dms/pass-through/api/rest/object?${params}&${makeQueryParams(properties)}`, { method: 'GET', }))
}

export async function parseLight(response: Promise<Response>) {
    return response
        .then(res => res.ok ? res.json() : Promise.reject({ status: 'error', message: res.status }))
}

/// TODO: status: 'unauthorized' is not handled
export async function parseResponse(response: Promise<Response>) {
    return response
        .then(res => res.ok ? res.json() : Promise.reject({ status: 'error', message: res.status }))
        .then(res => {
            if (res.success) {
                if (res.data === undefined) {
                    console.warn('No data in response')
                    return {}
                } else {
                    return res.data
                }
            } else {
                return Promise.reject({ status: 'error', message: res.message })
            }
    })
}


function makeQueryParams(properties: string[]) {
    return ['uuid', 'name', 'isFolder', 'formatObject.mimeType', 'description', 'createDate', 'updateDate', ...properties].map(p => `properties=${p}`).join('&')
}

function getSource(uuid?: string, root?: string) {
    const source = uuid 
        ? `uuid:${uuid}` 
        : encodeURIComponent(root ?? 'agorum/roi/Files')
        /*
        Pfad mit %2F codieren (aber lieber uuid von root speichern) oder root mit “9999” finden (immer fixe ID).
        */
    return source
}

function getFilePath(source: string, properties: string[] = []) {
    const params = makeQueryParams(properties)
    const path = `/api/dms/pass-through/api/rest/object/${source}?${params}`
    const basicKey = ['dms', source]
    const queryKey: string[] = [...basicKey, params]
    return {basicKey, queryKey, path}
}

export function clientAreaPath(number: string) {
    const groups = _.words(number, /.{2}/g)
    return `client-areas/${groups.join('/')}/client_${number}`
}

export function getClientRoot(number: string) {
    if (!number) return undefined

    const root = `agorum/roi/Files/${clientAreaPath(number)}`
    return root
}

type AugmentedAgorumObject<T extends string> = AgorumObject & Record<T, any>

export function useAgorumObject<T extends string>({uuid, root, properties = []}: {uuid?: string, root?: string, properties?: T[]}) {
    const {setErrorMessage} = useMessages()
    const {setStrike} = useAuth() // TODO: necessary for others as well?
    const queryClient = useQueryClient()

    // TODO: Do we need to check if uuid or root is defined?
    
    const source = getSource(uuid, root)

    const {path, queryKey, basicKey} = getFilePath(source, properties)

    const queryFn = async () => parseResponse(fetch(path, { method: 'GET', }))
        .then(res => { setStrike(0); return res })
        .catch(err => {
            setErrorMessage(err.message)
            if (err.status === 'unauthorized') {
                setStrike(s => s + 1)
            }
            return {}
        })

    const {data: object, error, isLoading} = useQuery<AgorumObject, ErrorMessage, AgorumObject>({
        queryKey,
        queryFn,
        enabled: (uuid !== undefined || root !== undefined)
    })
    if (error) {
        console.error(error)
        setErrorMessage(error.message)
    }

    const postMutation = useMutation<AgorumObject, ErrorMessage, Record<string, any>>({
        mutationFn: (payload: Record<string, any>) => updateObject(uuid, payload),
        onMutate: async (payload) => {
            await queryClient.cancelQueries({queryKey})
            await queryClient.cancelQueries({queryKey: basicKey})
            const formerValue = queryClient.getQueryData<AgorumObject>(queryKey)
            queryClient.setQueryData(queryKey, (old: AgorumObject) => ({...old, ...payload}))
            return { formerValue }
        },
        onError: (err, payload, context: {formerValue: AgorumObject}) => {
            queryClient.setQueryData(queryKey, context?.formerValue)
            setErrorMessage(err.message)
        },
        onSettled: () => {
            queryClient.invalidateQueries({queryKey: basicKey})
        },
    })

    async function deleteObj() {
        await queryClient.cancelQueries({queryKey})
        const resp = await deleteObject(uuid)
        queryClient.invalidateQueries({queryKey: basicKey})
        return resp
    }

    async function revert() {
        const resp = await revertObject(uuid)
        queryClient.invalidateQueries({queryKey: basicKey})
        return resp
    }

    //console.log({object})

    return {
        object: object ? {...object as AugmentedAgorumObject<T>, 'contentType': object['formatObject.mimeType']} : undefined,
        revert,
        update: postMutation.mutate,
        deleteObject: deleteObj, //deleteMutation.mutate,
        error, isLoading
    }
}

function getFolderPath(object?: {uuid: string}) {
    const path = `/api/dms/pass-through/api/rest/object?${makeQueryParams([])}&source=uuid:${object?.uuid}&provider=folder`
    const queryKey: string[] = ['dms', 'folder', object?.uuid ?? '']
    return  {queryKey, path}
}

export function useAgorumFolder(object?: AgorumObject) {
    const {setErrorMessage} = useMessages()
    
    const {path, queryKey} = getFolderPath(object)

    //const queryKey: string[] = [path]
    const queryFn = async () => parseResponse(fetch(path, { method: 'GET', }))

    const {data: folder, error, isLoading} = useQuery<AgorumObject[], ErrorMessage, AgorumObject[]>({
        queryKey,
        queryFn,
        enabled: object?.isFolder === true
    })

    if (error) {
        console.error(error)
        setErrorMessage(error.message)
    }

    return { 
        folder,
        error, 
        isLoading: (object?.isFolder && isLoading),
    }
}

export function useFolderReload(object?: {uuid: string}) {
    const client = useQueryClient()
    const {queryKey} = getFolderPath(object)

    return {
        reload: (uuid?: string) => {
            const qk = uuid === undefined ? queryKey : getFolderPath({ uuid }).queryKey
            return client.invalidateQueries({queryKey: qk})
        },
    }
}

export function useFileReload() {
    const client = useQueryClient()
    return {
        reload: (uuid: string) => {
            const source = getSource(uuid)
            const {queryKey} = getFilePath(source, [])
            client.invalidateQueries({queryKey: queryKey.slice(0, 2)})
        },
    }

}

export async function addFolder({uuid, foldername, description}: {uuid: string, foldername: string, description?: string}): Promise<{id: number}> {
    const formdata = new FormData()
    formdata.append('source', uuid)
    formdata.append('handler', 'folder')
    formdata.append('data', JSON.stringify({name: htmlEncode(foldername), description}))

    return parseResponse(fetch(`/api/dms/pass-through/api/rest/object`, {
        method: 'POST',
        body: formdata,
        headers: { 'Accept': 'application/json' },
    }))
}

export async function searchTrashcan({query = ''}: {query: string}) {
    return parseLight(fetch(`/api/dms/pass-through/api/rest/custom/aipm.patent.cockpit.api.trashcan?query=${query}`))
}

export async function deleteObject(uuid: string) {
    // This should only move it to the trash
    const params = encodeURIComponent(JSON.stringify({trash: true}))
    return parseResponse(fetch(`/api/dms/pass-through/api/rest/object/${uuid}?parameters=${params}`, { method: 'DELETE', }))
}

function revertObject(uuid: string) {

    return parseResponse(fetch(`/api/dms/pass-through/api/rest/custom/aipm.patent.cockpit.api.revert?source=${uuid}`))
}

export async function createPreviewTicket(uuid: string) {
    const params = {source: `uuid:${uuid}`, provider: 'preview'}
    return parseResponse(fetch(`/api/dms/pass-through/api/rest/object/?${createSearchParams(params)}`, { method: 'GET', }))
}

export async function renameObject(uuid: string, name: string) {
    return updateObject(uuid, {name})
}

async function updateObject(uuid: string, data: Record<string, any>) {
    const formdata = new FormData()
    formdata.append('data', JSON.stringify(data))
    formdata.append('updatehandler', 'object')

    return parseResponse(fetch(`/api/dms/pass-through/api/rest/object/${uuid}`, {
        method: 'PUT',
        body: formdata,
        headers: { 'Accept': 'application/json' },
    }))
}

export async function moveObject(uuid: string, target: string, parent: string) {
    const formdata = new FormData()
    formdata.append('handler', 'object')
    formdata.append('target', 'uuid:' + target)
    formdata.append('parameters', JSON.stringify({parent: [parent]}))

    return parseResponse(fetch(`/api/dms/pass-through/api/rest/object/move/uuid:${uuid}`, {
        method: 'POST',
        body: formdata,
        headers: { 'Accept': 'application/json' },
    }))
}

export async function search({query, limit = 10, start}: {query: string, limit?: number, start?: string}) {
    const params = createSearchParams({
        query: encodeURIComponent(query),
        //includeTrash: 'true', // TODO
        ...(limit ? {limit: limit.toString()} : {}),
        ...(start ? {start} : {}),
    })
    
    return parseLight(fetch(`/api/dms/pass-through/api/rest/custom/aipm.patent.cockpit.api.search?${params.toString()}`))
}

export async function uploadFile(file: File, uuid: string, sessionId: string, baseurl: string) {
    const formData = new FormData()
    formData.append('filename', file)

    const params = {
        name: file.name,
        target: 'uuid:' + uuid,
        sessionId,
    }
    const path = `https://${baseurl}/api/rest/custom/aipm.patent.cockpit.api.upload?${createSearchParams(params)}`

    return fetch(path, {
        method: "POST",
        body: formData,
    })
}
/*
// Example response
{
  "pathParts" : [
    "Patents",
    "P-002",
    "P-002US"
  ],
  "uuid" : "4472f590-fe1f-11ee-b1bd-00000a6d680a",
  "parents" : [
    {
      "name" : "P-002US",
      "uuid" : "384b6310-fe1f-11ee-b1bd-00000a6d680a"
    },
    {
      "name" : "P-002",
      "uuid" : "37e638a0-fe1f-11ee-b1bd-00000a6d680a"
    },
    {
      "name" : "Patents",
      "uuid" : "b88d1720-fc0e-11ee-b1bd-00000a6d680a"
    }
  ]
}
*/

export async function findParent(number: string, entity: string, parent: string, parentName?: string) {
    const parentFolder = await findUuid(number, entity, htmlEncode(parent))
    //console.log({parentFolder})
    if (parentFolder === undefined) {
        await createFamilyFolder({ number, entity, internalReference: htmlEncode(parent), description: parentName})
        return findParent(number, entity, parent)
    } else {
        return parentFolder
    }
}


export type GenericFamily = {internalReference: string, description: string} & {members: {internalReference: string}[]}
export async function createPortfolioFolderStructure(portfolio: GenericFamily[], entity: string, number: string, callback = (() => {}) as (idx: number, count: number) => void) {
    const ipRootUuid = await findIpRootFolder({number, entity})
    for (const [idx, family] of portfolio.entries()) {
        let familyFolder = await findUuid(number, entity, family.internalReference)
        if (familyFolder === undefined) {
            const result = await addFolder({uuid: ipRootUuid!, foldername: family.internalReference, description: family.description})
            familyFolder = await findUuid(number, entity, family.internalReference)
        }
        for (const member of family.members) {
            const uuid = await findUuid(number, entity, member.internalReference, family.internalReference)
            if (uuid === undefined || uuid === '') {
                await addFolder({ uuid: familyFolder, foldername: member.internalReference })
            }
        }
        callback(idx, portfolio.length)
    }
}

// This function returns the UUIDs of all the folders in the path (?)
export async function parentPaths(uuid: string, pathParts: string[]): Promise<{parents: {name: string, uuid: string}[]}> {
    const path = `/api/dms/pass-through/api/rest/custom/aipm.patent.cockpit.api.pathParts?uuid=${uuid}&pathParts=${encodeURIComponent(JSON.stringify(pathParts))}`
    return parseLight(fetch(path))
}

export function downloadLink({uuid}: {uuid: string}) {
    return `/api/dms/download/api/rest/object/download/${uuid}`
}

export function adaptMailEmbeddedImageSrc(src: string) {
    // this is cid:uuid%3A%2F%2F followed by the uuid, optionally followed by a string starting with @
    return src.replace(/cid:uuid%3A%2F%2F([a-f0-9-]+)(@[0-9a-zA-Z.]+|)/g, '/api/dms/download/api/rest/object/download/$1');
}